import type { HskBand } from "../hsk/hsk-band.js";
import { hskBands } from "../hsk/hsk-band.js";
import { rewriteKey } from "../../fuxi/grade-rewrite.js";
import type { ItemWriting } from "./yufa-written-item.js";
import { recordOf, writingsOf } from "./yufa-writing-read.js";

/** How an answer relates to the task and its stated context. */
export type AnswerStatus = "target" | "valid-other" | "error" | "unverified";

interface GrammarAnswerBase {
  readonly id: string;
  readonly status: AnswerStatus;
  readonly meaning: string;
  readonly feedback: string;
}

/** Older Chinese records omit language. English forms retain their own text. */
export type GrammarAnswer = GrammarAnswerBase &
  (
    | { readonly language?: "zh"; readonly forms: readonly ItemWriting[] }
    | { readonly language: "en"; readonly forms: readonly string[] }
  );

/** Preserve word boundaries and punctuation when comparing English choices. */
export function answerKey(text: string, language: "zh" | "en" = "zh"): string {
  return language === "en" ? text.normalize("NFC").trim() : rewriteKey(text);
}

/** Both scripts name the same Chinese answer. English needs no script conversion. */
export function answerTexts(answer: GrammarAnswer): readonly string[] {
  return answer.language === "en"
    ? answer.forms
    : answer.forms.flatMap((form) => [form.simplified, form.traditional]);
}

/** Learner-facing metadata shared by authored grammar tasks. */
export interface GrammarTeaching {
  readonly version: 1;
  readonly target: { readonly id: string; readonly use: string };
  readonly task:
    | "select"
    | "rewrite"
    | "combine"
    | "place"
    | "interpret"
    | "reply"
    | "repair";
  readonly level: HskBand;
  readonly context: string;
  readonly answers: readonly GrammarAnswer[];
  readonly hints?: readonly string[];
  readonly family?: {
    readonly id: string;
    readonly members: readonly string[];
  };
}

/** Text that contains something other than whitespace. */
export function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/** A nonempty list of text values. */
export function textList(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.length > 0 && value.every(hasText);
}

const statuses: readonly unknown[] = [
  "target",
  "valid-other",
  "error",
  "unverified",
];
const tasks: readonly unknown[] = [
  "select",
  "rewrite",
  "combine",
  "place",
  "interpret",
  "reply",
  "repair",
];
const namePattern = /^[a-z0-9]+(?:[-/][a-z0-9]+)*$/u;

function answerOf(value: unknown): GrammarAnswer | undefined {
  const record = recordOf(value);
  const language = record?.["language"];
  const forms =
    language === "en"
      ? textList(record?.["forms"])
        ? record["forms"]
        : undefined
      : writingsOf(record?.["forms"]);

  return record !== undefined &&
    (language === undefined || language === "zh" || language === "en") &&
    forms !== undefined &&
    hasText(record["id"]) &&
    namePattern.test(record["id"]) &&
    statuses.includes(record["status"]) &&
    hasText(record["meaning"]) &&
    hasText(record["feedback"])
    ? ({
        id: record["id"],
        forms,
        status: record["status"] as AnswerStatus,
        meaning: record["meaning"],
        feedback: record["feedback"],
        ...(language === undefined ? {} : { language }),
      } as GrammarAnswer)
    : undefined;
}

/** Refuse meanings that the current marker would collapse into one answer. */
function separateAnswers(answers: readonly GrammarAnswer[]): boolean {
  const keys = new Map<string, string>();
  const ids = new Set<string>();

  for (const answer of answers) {
    if (ids.has(answer.id)) {
      return false;
    }
    ids.add(answer.id);
    for (const text of answerTexts(answer)) {
      const normalized = answerKey(text, answer.language);
      const key = `${answer.language ?? "zh"}:${normalized}`;
      const held = keys.get(key);
      if (normalized === "" || (held !== undefined && held !== answer.id)) {
        return false;
      }
      keys.set(key, answer.id);
    }
  }
  return answers.some((answer) => answer.status === "target");
}

/** Read metadata without converting or deduplicating any answer forms. */
export function teachingOf(value: unknown): GrammarTeaching | undefined {
  const record = recordOf(value);
  const target = recordOf(record?.["target"]);
  if (
    record === undefined ||
    record["version"] !== 1 ||
    target === undefined ||
    !hasText(target["id"]) ||
    !namePattern.test(target["id"]) ||
    !hasText(target["use"]) ||
    !tasks.includes(record["task"]) ||
    !hskBands.includes(record["level"] as HskBand) ||
    !hasText(record["context"]) ||
    !Array.isArray(record["answers"])
  ) {
    return undefined;
  }
  const answers = record["answers"].map(answerOf);
  if (
    !answers.every((answer) => answer !== undefined) ||
    !separateAnswers(answers)
  ) {
    return undefined;
  }
  const hints = record["hints"];
  if (hints !== undefined && !textList(hints)) {
    return undefined;
  }
  const family = recordOf(record["family"]);
  if (
    record["family"] !== undefined &&
    (family === undefined ||
      !hasText(family["id"]) ||
      !namePattern.test(family["id"]) ||
      !textList(family["members"]) ||
      new Set(family["members"]).size !== family["members"].length)
  ) {
    return undefined;
  }
  return {
    version: 1,
    target: { id: target["id"], use: target["use"] },
    task: record["task"] as GrammarTeaching["task"],
    level: record["level"] as HskBand,
    context: record["context"],
    answers,
    ...(hints === undefined ? {} : { hints }),
    ...(family === undefined
      ? {}
      : {
          family: {
            id: family["id"] as string,
            members: family["members"] as readonly string[],
          },
        }),
  };
}

/** An unlisted response has no reviewed explanation. */
export function grammarAnswer(
  teaching: GrammarTeaching,
  typed: string,
  language: "zh" | "en" = "zh",
): GrammarAnswer | undefined {
  const key = answerKey(typed, language);
  return teaching.answers.find(
    (answer) =>
      (answer.language ?? "zh") === language &&
      answerTexts(answer).some((text) => answerKey(text, language) === key),
  );
}

const forms = (values: readonly ItemWriting[]): string[] =>
  values
    .map((form) => JSON.stringify([form.simplified, form.traditional]))
    .sort();

/** Check that the metadata describes exactly the answers the marker accepts. */
export function teachingMatches(
  teaching: GrammarTeaching,
  accepted: readonly ItemWriting[],
  wrong: readonly ItemWriting[] = [],
): boolean {
  if (teaching.answers.some((answer) => answer.language === "en")) {
    return false;
  }
  const target = teaching.answers
    .filter((answer) => answer.status === "target")
    .flatMap((answer) => (answer.language === "en" ? [] : answer.forms));

  return (
    JSON.stringify(forms(target)) === JSON.stringify(forms(accepted)) &&
    wrong.every((form) =>
      teaching.answers.some(
        (answer) =>
          answer.status !== "target" &&
          answer.language !== "en" &&
          answer.forms.some(
            (each) =>
              each.simplified === form.simplified &&
              each.traditional === form.traditional,
          ),
      ),
    )
  );
}
